import { getStepInstruments } from "src/Requests/stepRequests";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { getCCOrderVolumeCalculationOptions } from "src/Utils/ccOrderVolumeCalculationTypes";
// Common Components
import {
  IStepOptionDropdownItem,
  StepOrderSide,
} from "src/Interfaces/stepInterfaces";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CCOrderSendOrderType,
  ICCOrderAddOrderFormValues,
  CCOrderCriterionType,
  ICCOrderCriterionTypes,
  ICCOrderLeg,
  ICCOrderRules,
  IGetCCOrder,
  VolumeCalculationType,
} from "src/Interfaces/ccOrder";
import {
  Button,
  Form,
  FullScreenLoading,
  Modal,
  StaticDropdown,
  TextInput,
} from "src/Components";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import {
  addCCOrder,
  getCCOrderCriterionTypes,
  getCCOrderRules,
  getCCOrders,
} from "src/Requests/ccOrder";
import {
  fallbackCCOrderCriterionTypes,
  getCCOrderCriterionOptions,
  getCCOrderCriterionTypeName,
  getDefaultCCOrderCriterionType,
} from "src/Utils/ccOrderCriterionTypes";
import { toast } from "react-toastify";
import SahraConnections from "src/Pages/Sahra/SahraConnections";
import { deltaHedgeOrderTypesDropdownOptions } from "src/Pages/DeltaHedgeOrder/DeltaHedgeOrder";
import { DeltaHedgeOrderType } from "src/Interfaces/deltaHedgeOrder";
import { RollReport } from "src/Interfaces/coveredCall";
import OrderPreviewTable, { isCCOrderPreviewRowSendable } from "src/Pages/CCOrder/OrderPreviewTable";
import PortfoGroupUserMultiSelectDropdown from "src/Components/Dropdown/Users/PortfoGroupUserMultiSelectDropdown";
import CriterionTypeDefaultSetting from "src/Pages/CCOrder/CriterionTypeDefaultSetting";

interface IProps {
  row: RollReport;
  onAction: () => void;
  volumeCalculationType: VolumeCalculationType;
}

enum ModalMode {
  Closed,
  SahraOrderTable,
  OrderPreview,
}

const sendOrderTypeOptions = [
  { id: CCOrderSendOrderType.Automatic, name: "\u062E\u0648\u062F\u06A9\u0627\u0631" },
  { id: CCOrderSendOrderType.Credit, name: "\u0627\u0639\u062A\u0628\u0627\u0631\u06CC" },
  { id: CCOrderSendOrderType.NonCredit, name: "\u063A\u06CC\u0631 \u0627\u0639\u062A\u0628\u0627\u0631\u06CC" },
];

const SendCCOrders = ({ row, onAction, volumeCalculationType }: IProps) => {
  const [formValues, setFormValues] = useState({
    coveredCallOrderLegs: [] as ICCOrderLeg[],
    coveredCallOrderSellLeg: {} as ICCOrderLeg,
  } as IGetCCOrder);

  const [instrumentOptions, setInstrumentOptions] = useState(
    [] as IStepOptionDropdownItem[],
  );

  const [modalMode, setModalMode] = useState(ModalMode.Closed);

  const [CCOrders, setCCOrders] = useState<ICCOrderAddOrderFormValues[]>([]);
  const [rules, setRules] = useState({} as ICCOrderRules);
  const [criterionTypes, setCriterionTypes] = useState<ICCOrderCriterionTypes>(
    fallbackCCOrderCriterionTypes,
  );
  const hasUserChangedCriterionType = useRef(false);

  const [loading, setLoading] = useState(false);

  const roundToMax3Decimals = (value: number) =>
    Math.round((value + Number.EPSILON) * 1000) / 1000;

  const toNumber = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number")
      return Number.isFinite(value) ? roundToMax3Decimals(value) : 0;

    const normalized = value
      .replace(/,/g, "")
      .replace(/٪|%/g, "")
      .trim()
      .split(/\s+/)[0];
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? roundToMax3Decimals(parsed) : 0;
  };

  const getCriterionValue = (
    criterionType: number | string | null | undefined,
  ) => {
    switch (Number(criterionType)) {
      case CCOrderCriterionType.MaxRollCost:
        return toNumber(row.rollPairPosition);

      case CCOrderCriterionType.MinDailyRollReturn:
        return toNumber(row.dailyRollGain);

      case CCOrderCriterionType.RollTimeDecayInvolveddFundRatioDaily:
        return toNumber(row.rollTimeDecayInvolveddFundRatioDaily);

      case CCOrderCriterionType.DailyNetGainTimeRangeRollPercent:
        return toNumber(row.dailyNetGainTimeRangeRollPercent);

      default:
        return 0;
    }
  };

  const orderSideDropdown = [
    { id: StepOrderSide.Buy, name: "خرید" },
    { id: StepOrderSide.Sell, name: "فروش" },
  ] as DropdownItem[];

  useEffect(() => {
    getStepInstruments()
      .then((resp) => {
        setInstrumentOptions(resp);
        getInitialFormValues();
      })
      .catch(packetErrorHandler);
    getCCOrderRules().then(setRules).catch(packetErrorHandler);
    getCCOrderCriterionTypes()
      .then(setCriterionTypes)
      .catch(packetErrorHandler);
  }, []);

  const criterionOptions = useMemo(
    () => getCCOrderCriterionOptions(criterionTypes, formValues?.orderType),
    [criterionTypes, formValues?.orderType],
  );

  useEffect(() => {
    if (!formValues?.orderType) return;

    const isAllowedType = criterionOptions.some(
      (item) => item.id?.toString() === formValues?.criterionType?.toString(),
    );

    const defaultCriterionType = getDefaultCCOrderCriterionType(
      criterionTypes,
      formValues.orderType,
    );
    if (!defaultCriterionType) return;
    if (hasUserChangedCriterionType.current && isAllowedType) return;
    if (formValues?.criterionType?.toString() === defaultCriterionType.toString())
      return;

    setFormValues((prev) => ({
      ...prev,
      criterionType: defaultCriterionType,
      criterion: getCriterionValue(defaultCriterionType),
    }));
  }, [
    criterionOptions,
    criterionTypes,
    formValues?.orderType,
    formValues?.criterionType,
  ]);

  function getInitialFormValues() {
    const res = {
      coveredCallOrderLegs: [] as ICCOrderLeg[],
      coveredCallOrderSellLeg: {} as ICCOrderLeg,
      orderType: DeltaHedgeOrderType.Roll,
      criterionType: getDefaultCCOrderCriterionType(
        criterionTypes,
        DeltaHedgeOrderType.Roll,
      ),
      criterion: getCriterionValue(
        getDefaultCCOrderCriterionType(criterionTypes, DeltaHedgeOrderType.Roll),
      ),
      involvedFund: 0,
      marginPercent: 0,
      sendOrderType: CCOrderSendOrderType.Automatic,
      blockSize: 100,
      portfoPercent: 100,
      coveredCallPositionStrategyEnum: row.coveredCallPositionStrategyEnum,
      accountIds: row.userIds,
      volumeCalculationType,
    } as IGetCCOrder;

    res.coveredCallOrderLegs = [
      {
        instrumentId: row.insIds[0],
        orderSide:
          row.sourceOrderSide === "خرید"
            ? StepOrderSide.Buy
            : StepOrderSide.Sell,
      },
    ];

    res.coveredCallOrderSellLeg = {
      instrumentId: row.insIds[1],
      orderSide:
        row.destOrderSide === "خرید" ? StepOrderSide.Buy : StepOrderSide.Sell,
    };

    setFormValues(res);
  }

  const changeHandler = (name: string, value: any) => {
    if (name === "volumeCalculationType")
      setFormValues({
        ...formValues,
        [name]: value,
        totalVolume: value?.toString() === "1" ? 100 : (undefined as any),
        portfoPercent: value?.toString() !== "1" ? 100 : (undefined as any),
      });
    else if (name === "criterionType") {
      hasUserChangedCriterionType.current = true;
      setFormValues({
        ...formValues,
        criterionType: value,
        criterion: getCriterionValue(value),
      });
    }
    else setFormValues({ ...formValues, [name]: value });
  };

  const sendCCOrders = () => {
    if (loading) return;

    setLoading(true);
    const requests = CCOrders.filter(isCCOrderPreviewRowSendable).map((order) =>
      addCCOrder({ ...order, accountIds: [order.accountId] }),
    );
    Promise.all(requests)
      .then(() => {
        toast.success("با موفقیت انجام شد");
        onAction();
        setModalMode(ModalMode.Closed);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const getOrders = () => {
    return CCOrders;
  };

  const sahraUserTableModalUI = (
    <div className="flex flex-col justify-start items-center gap-3">
      <SahraConnections
        initialSearchParam={{
          userIds: CCOrders.filter(isCCOrderPreviewRowSendable).map((order) => order.accountId),
        }}
      />

      <div className="w-[100px] mr-auto">
        <Button
          text="ثبت"
          variant="contained"
          onClick={sendCCOrders}
          disabled={loading}
          loading={loading}
        />
      </div>
    </div>
  );

  const orderPreviewModalUI = (
    <div className="flex flex-col justify-start items-center gap-3">
      <OrderPreviewTable
        values={CCOrders}
        coveredCallOrderType={DeltaHedgeOrderType.Roll}
      />

      <div className="w-[100px] mr-auto">
        <Button
          text="ثبت"
          variant="contained"
          onClick={() => setModalMode(ModalMode.SahraOrderTable)}
          disabled={!CCOrders.some(isCCOrderPreviewRowSendable)}
        />
      </div>
    </div>
  );

  const getModalMode = () => {
    switch (modalMode) {
      case ModalMode.Closed:
        return <></>;

      case ModalMode.SahraOrderTable:
        return sahraUserTableModalUI;

      case ModalMode.OrderPreview:
        return orderPreviewModalUI;

      default:
        return <></>;
    }
  };

  function submitHandler(): void {
    if (loading) return;

    setLoading(true);
    getCCOrders(formValues)
      .then((r) => {
        setCCOrders(
          r.map((order) => ({
            ...order,
            criterionType: order.criterionType ?? formValues.criterionType,
          })),
        );
        setModalMode(ModalMode.OrderPreview);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }
  const doNothing = () => {};

  const volumeCalculationOptions = getCCOrderVolumeCalculationOptions(
    rules.volumeCalculationTypesByProcess?.[
      Number(formValues?.orderType || -1)
    ],
  );
  const volume =
    volumeCalculationOptions.find(
      (item) => item.id === formValues?.volumeCalculationType,
    )?.name ?? "حجم";

  return (
    <Form className="!w-96 gap-3 p-2 px-2 !mx-auto">
      {loading ? <FullScreenLoading /> : <></>}
      {modalMode !== ModalMode.Closed ? (
        <Modal
          title="ارسال سفارش"
          modalBoxClassName="!min-w-0 w-[1320px]"
          onClose={() => setModalMode(ModalMode.Closed)}
          UI={getModalMode()}
        />
      ) : (
        <></>
      )}

      <PortfoGroupUserMultiSelectDropdown
        name="accountIds"
        label="نام اکانت ها"
        value={formValues?.accountIds}
        onChange={changeHandler}
        withTagBox
        extraLabel
      />

      <div className="w-full grid grid-cols-2 gap-1">
        <StaticDropdown
          dropdown={{
            name: "coveredCallOrderType",
            placeholder: "فرایند",
            extraLabel: true,
            disabled: true,
          }}
          onChange={changeHandler}
          options={deltaHedgeOrderTypesDropdownOptions}
          value={formValues.orderType}
        />
        <div />

        <StaticDropdown
          onChange={doNothing}
          dropdown={{
            name: "",
            placeholder: " جهت اول",
            disabled: true,
            extraLabel: true,
          }}
          value={formValues.coveredCallOrderLegs[0]?.orderSide}
          options={orderSideDropdown}
        />
        <StaticDropdown
          onChange={doNothing}
          dropdown={{
            name: "",
            placeholder: " جهت دوم",
            disabled: true,
            extraLabel: true,
          }}
          value={formValues.coveredCallOrderSellLeg?.orderSide}
          options={orderSideDropdown}
        />

        <StaticDropdown
          value={
            formValues?.coveredCallOrderLegs &&
            formValues?.coveredCallOrderLegs[0]
              ? formValues?.coveredCallOrderLegs[0].instrumentId
              : ""
          }
          onChange={doNothing}
          dropdown={{
            name: "instrumentId",
            placeholder: `نماد اول`,
            extraLabel: true,
            disabled: true,
          }}
          options={instrumentOptions}
        />

        <StaticDropdown
          value={formValues?.coveredCallOrderSellLeg.instrumentId}
          onChange={doNothing}
          dropdown={{
            name: "instrumentId",
            placeholder: `نماد دوم`,
            extraLabel: true,
            disabled: true,
          }}
          options={instrumentOptions}
        />
      </div>

      <div className="w-full flex flex-row justify-start gap-1">
        <StaticDropdown
          dropdown={{
            name: "volumeCalculationType",
            placeholder: "روش تعیین حجم",
            extraLabel: true,
          }}
          value={formValues?.volumeCalculationType}
          onChange={changeHandler}
          options={volumeCalculationOptions}
        />
        <TextInput
          label={volume}
          name={
            formValues?.volumeCalculationType?.toString() === "1"
              ? "totalVolume"
              : "portfoPercent"
          }
          onChange={changeHandler}
          value={formValues?.[
            formValues?.volumeCalculationType?.toString() === "1"
              ? "totalVolume"
              : "portfoPercent"
          ]?.toString()}
          extraLabel
        />
      </div>

      {criterionOptions.length > 1 ? (
        <div className="w-full flex items-end gap-1">
          <div className="flex-1">
            <StaticDropdown
              dropdown={{
                name: "criterionType",
                placeholder: "نوع معیار",
                extraLabel: true,
              }}
              onChange={changeHandler}
              options={criterionOptions}
              value={formValues?.criterionType}
            />
          </div>
          <CriterionTypeDefaultSetting
            orderType={formValues?.orderType}
            criterionTypes={criterionTypes}
            criterionOptions={criterionOptions}
            onSaved={(updatedCriterionTypes, criterionType) => {
              setCriterionTypes(updatedCriterionTypes);
              changeHandler("criterionType", criterionType);
            }}
          />
        </div>
      ) : (
        <></>
      )}
      <TextInput
        label={`معیار: ${getCCOrderCriterionTypeName(criterionTypes, formValues?.orderType, formValues?.criterionType)}`}
        name="criterion"
        onChange={changeHandler}
        value={formValues?.criterion?.toString()}
        classname="ltr text-right"
        extraLabel
      />

      <TextInput
        label="% بلوک"
        value={formValues.blockSize?.toString()}
        name="blockSize"
        onChange={changeHandler}
        extraLabel
      />

      <TextInput
        label="% مارجین"
        value={formValues.marginPercent?.toString()}
        name="marginPercent"
        onChange={changeHandler}
        extraLabel
      />
      <StaticDropdown
        dropdown={{
          name: "sendOrderType",
          placeholder: "\u062A\u0631\u062A\u06CC\u0628 \u0627\u0631\u0633\u0627\u0644",
          extraLabel: true,
        }}
        onChange={changeHandler}
        options={sendOrderTypeOptions}
        value={formValues.sendOrderType ?? CCOrderSendOrderType.Automatic}
      />
      <hr />
      <Button
        text="ثبت"
        variant="contained"
        onClick={submitHandler}
        disabled={
          loading ||
          !formValues?.volumeCalculationType ||
          !formValues?.accountIds?.length
        }
        loading={loading}
      />
    </Form>
  );
};

export default SendCCOrders;
